import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { get, set } from "lodash";

import formatCurrency from "@tvg/formatter/currency";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import { BetStatusCodesEnum } from "@tvg/ts-types/Bet";
import { RaceStatusEnum } from "@tvg/ts-types/Race";

import { isRaceOff } from "@tvg/sh-lib-my-bets/utils/raceDetails";
import { getCurrentProbableValue } from "@tvg/sh-lib-my-bets/utils/potentialReturn";
import {
  cleanBetCancelResult,
  openBetCancelModal
} from "@tvg/sh-lib-my-bets/redux/actions";
import { BetTypeCell, Breed } from "@tvg/design-system";
import BetUtils from "@tvg/utils/betSelection";
import { onRepeatBetGTM } from "@tvg/sh-lib-my-bets/utils/gtm";
import { getBettingInfo } from "@tvg/desktop-bet/src/utils/betUtils";
import { checkWagerIsCancelable } from "@tvg/sh-lib-my-bets/utils/general";
import { WroWager } from "@tvg/ts-types/WroWager";
import { getSelectionsRunnerNbr } from "../../../../utils/raceUtils";
import { cancelBetGtmEvent } from "../../../../utils/gtm/betCancel";
import { getIsBetRepeatable } from "../helpers";
import { BetInfoActionsProps, BetInfoProps } from "../types";
import useGeocomplyBetDisabled from "../../../ConfirmBetModal/hooks/useGeocomplyBetDisabled";

export const BetInfo = ({
  race,
  races,
  wager,
  betValue,
  onRepeatWager,
  onCancelWager,
  qaLabel,
  ...rest
}: BetInfoProps) => {
  const dispatch = useDispatch();

  const { isBetDisabled } = useGeocomplyBetDisabled();

  const selectionsRunnerNbr = getSelectionsRunnerNbr(wager);
  const approximatedPayout = getCurrentProbableValue(
    get(race, "probables", []),
    get(wager, "wagerType"),
    get(wager, "wagerAmount", 0),
    selectionsRunnerNbr
  );

  const typeFromRace = BetUtils.getOriginalBetTypeFromRace(
    race,
    wager.wagerType.id as unknown as WagerTypeCodesEnum
  );

  const { numberOfWagerableRunners, fullSelections } = getBettingInfo(
    selectionsRunnerNbr,
    wager.wagerAmount,
    race,
    races,
    typeFromRace
  );

  const scratches = BetUtils.getSelectionsRunnerNbrScratched(fullSelections);

  const onRepeatBetHandler = useCallback(() => {
    onRepeatWager(wager);
    onRepeatBetGTM({
      gaEventAction: "My Bets CTA Clicked",
      gaEventLabel: "repeat bet",
      module: "Race Bets"
    });
  }, [wager]);

  const onCancelBetHandler = useCallback(() => {
    onCancelWager(wager);
    dispatch(cleanBetCancelResult());
    dispatch(
      openBetCancelModal({
        bet: {
          value: betValue,
          wagers: [wager]
        },
        wager
      })
    );
    cancelBetGtmEvent();
  }, [wager]);

  const getBetActions = useCallback(() => {
    const actions: BetInfoActionsProps = {};
    const raceCode = race.status?.code;

    if (!raceCode) {
      return actions;
    }

    if (!isRaceOff(race.status?.code || RaceStatusEnum.OPEN)) {
      set(actions, "onRepeatBetHandler", onRepeatBetHandler);
    }

    if (checkWagerIsCancelable(wager as unknown as WroWager, raceCode)) {
      set(actions, "onCancelBetHandler", onCancelBetHandler);
    }

    return actions;
  }, [wager, race.status?.code]);

  return (
    <BetTypeCell
      m="space-4"
      qaLabel={qaLabel}
      raceNumber={race.raceNumber}
      numWagerableRunners={numberOfWagerableRunners}
      completeSelection={fullSelections}
      selections={fullSelections[0]}
      scratches={scratches}
      approximatedPayout={approximatedPayout}
      betAmount={formatCurrency(wager.wagerAmount)}
      betRefund={formatCurrency(wager.betRefund)}
      betTicket={formatCurrency(+wager.betTotal)}
      breed={`${get(race, "type.name", "Thoroughbred")}`.toLowerCase() as Breed}
      type={get(typeFromRace, "type.code", WagerTypeCodesEnum.WIN)}
      isKey={get(typeFromRace, "isKey", false)}
      isBox={get(typeFromRace, "isBox", false)}
      isWheel={get(typeFromRace, "isWheel", false)}
      isLeg={get(typeFromRace, "legCount", 1) > 1}
      betStatus="NONE"
      betTypeName={get(typeFromRace, "type.name", WagerTypeCodesEnum.WIN)}
      betStatusCode={wager.betStatus.code as BetStatusCodesEnum}
      winAmountBet={formatCurrency(wager.totalWinAmount ?? 0)}
      isRepeatable={
        isBetDisabled
          ? false
          : getIsBetRepeatable(wager, race, races, typeFromRace, {
              blacklistedBets: []
            })
      }
      {...getBetActions()}
      {...rest}
    />
  );
};

export default BetInfo;
